import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@/context/localization-context";
import {
  Camera,
  Brain,
  BarChart3,
  CheckCircle,
  ArrowRight,
  Pizza,
  Eye,
  Layers,
  Globe,
} from "lucide-react";

export default function LandingPage() {
  const navigate = useNavigate();
  const { t, formatNumber } = useLocalization();

  const features = [
    {
      icon: Camera,
      title: t("landing.features.camera.title"),
      description: t("landing.features.camera.description"),
    },
    {
      icon: Brain,
      title: t("landing.features.evaluation.title"),
      description: t("landing.features.evaluation.description"),
    },
    {
      icon: Eye,
      title: t("landing.features.vision.title"),
      description: t("landing.features.vision.description"),
    },
    {
      icon: Layers,
      title: t("landing.features.visualization.title"),
      description: t("landing.features.visualization.description"),
    },
    {
      icon: BarChart3,
      title: t("landing.features.analytics.title"),
      description: t("landing.features.analytics.description"),
    },
    {
      icon: Globe,
      title: t("landing.features.integration.title"),
      description: t("landing.features.integration.description"),
    },
  ];

  const stats = [
    {
      label: t("landing.stats.consistency.label"),
      value: formatNumber(0.95, { style: "percent" }),
      description: t("landing.stats.consistency.description"),
    },
    {
      label: t("landing.stats.speed.label"),
      value: t("landing.stats.speed.value"),
      description: t("landing.stats.speed.description"),
    },
    {
      label: t("landing.stats.accuracy.label"),
      value: formatNumber(0.98, { style: "percent" }),
      description: t("landing.stats.accuracy.description"),
    },
    {
      label: t("landing.stats.efficiency.label"),
      value: formatNumber(0.8, { style: "percent" }),
      description: t("landing.stats.efficiency.description"),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-100/20 via-transparent to-transparent"></div>
        <div className="relative mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="absolute inset-0 bg-orange-200 rounded-full animate-pulse"></div>
                <div className="relative bg-orange-100 p-6 rounded-full">
                  <Pizza className="h-16 w-16 text-orange-600" />
                </div>
              </div>
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
              {t("landing.hero.titleLead")}
              <span className="text-orange-600"> {t("landing.hero.titleHighlight")}</span>
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600 max-w-2xl mx-auto">
              {t("landing.hero.description")}
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Button
                size="lg"
                onClick={() => navigate("/dashboard")}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {t("landing.hero.viewDashboard")}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="py-16 bg-white/50 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:max-w-none">
            <div className="text-center">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                {t("landing.stats.title")}
              </h2>
              <p className="mt-4 text-lg leading-8 text-gray-600">
                {t("landing.stats.subtitle")}
              </p>
            </div>
            <dl className="mt-16 grid grid-cols-1 gap-0.5 overflow-hidden rounded-2xl text-center sm:grid-cols-2 lg:grid-cols-4">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="flex flex-col bg-white p-8 shadow-sm hover:shadow-md transition-shadow"
                >
                  <dt className="text-sm font-semibold leading-6 text-gray-600">
                    {stat.label}
                  </dt>
                  <dd className="order-first text-3xl font-bold tracking-tight text-orange-600">
                    {stat.value}
                  </dd>
                  <dd className="text-sm text-gray-500 mt-2">
                    {stat.description}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-base font-semibold leading-7 text-orange-600">
              {t("landing.features.eyebrow")}
            </h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              {t("landing.features.title")}
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              {t("landing.features.subtitle")}
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <div className="grid grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
              {features.map((feature) => (
                <Card
                  key={feature.title}
                  className="hover:shadow-lg transition-shadow border-0 shadow-md"
                >
                  <CardHeader>
                    <div className="mb-4">
                      <div className="inline-flex p-3 rounded-xl bg-orange-100">
                        <feature.icon className="h-6 w-6 text-orange-600" />
                      </div>
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Technology Stack */}
      <div className="py-24 bg-gray-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              {t("landing.tech.title")}
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              {t("landing.tech.subtitle")}
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-5xl">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {[
                "React 18",
                "TypeScript",
                t("landing.tech.neuralNetworks"),
                t("landing.tech.computerVision"),
                "AWS S3",
                "Docker",
                "Tailwind CSS",
                t("landing.tech.restApis"),
                "OpenAPI",
                t("landing.tech.smartCamera"),
                t("landing.tech.iot"),
                t("landing.tech.realTime"),
              ].map((tech) => (
                <Badge
                  key={tech}
                  variant="secondary"
                  className="py-2 px-4 text-center justify-center"
                >
                  {tech}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Image Placeholder Section */}
      <div className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              {t("landing.showcase.title")}
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              {t("landing.showcase.subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Placeholder for system screenshots */}
            <div className="space-y-4">
              <div className="aspect-video bg-gradient-to-br from-orange-100 to-red-100 rounded-xl border-2 border-dashed border-orange-200 flex items-center justify-center">
                <div className="text-center">
                  <Eye className="h-12 w-12 text-orange-400 mx-auto mb-4" />
                  <p className="text-orange-600 font-medium">
                    {t("landing.showcase.capture.title")}
                  </p>
                  <p className="text-sm text-orange-500 mt-2">
                    {t("landing.showcase.capture.description")}
                  </p>
                </div>
              </div>
              <div className="aspect-video bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl border-2 border-dashed border-blue-200 flex items-center justify-center">
                <div className="text-center">
                  <Layers className="h-12 w-12 text-blue-400 mx-auto mb-4" />
                  <p className="text-blue-600 font-medium">
                    {t("landing.showcase.analysis.title")}
                  </p>
                  <p className="text-sm text-blue-500 mt-2">
                    {t("landing.showcase.analysis.description")}
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="aspect-video bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl border-2 border-dashed border-green-200 flex items-center justify-center">
                <div className="text-center">
                  <BarChart3 className="h-12 w-12 text-green-400 mx-auto mb-4" />
                  <p className="text-green-600 font-medium">
                    {t("landing.showcase.analytics.title")}
                  </p>
                  <p className="text-sm text-green-500 mt-2">
                    {t("landing.showcase.analytics.description")}
                  </p>
                </div>
              </div>
              <div className="aspect-video bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl border-2 border-dashed border-purple-200 flex items-center justify-center">
                <div className="text-center">
                  <CheckCircle className="h-12 w-12 text-purple-400 mx-auto mb-4" />
                  <p className="text-purple-600 font-medium">
                    {t("landing.showcase.results.title")}
                  </p>
                  <p className="text-sm text-purple-500 mt-2">
                    {t("landing.showcase.results.description")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-orange-600">
        <div className="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              {t("landing.cta.title")}
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-orange-200">
              {t("landing.cta.description")}
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Button
                size="lg"
                variant="secondary"
                onClick={() => navigate("/dashboard")}
                className="bg-white text-orange-600 hover:bg-orange-50"
              >
                {t("landing.cta.viewDemo")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <Pizza className="h-8 w-8 text-orange-400" />
            </div>
            <p className="text-sm leading-6 text-gray-400">
              {t("landing.footer.credits")}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              {t("landing.footer.tagline")}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
